import { useEffect, useState } from 'react';

import { CreateDocChronPreview } from './create-doc-chron-preview';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';

interface CreateDocChronologyDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (options: { includeKeyFacts: boolean; includeRelevantFacts: boolean; includeAllFacts: boolean }) => void;
  isCreating: boolean;
  description: string;
}

export const CreateDocChronologyDialog = ({
  isOpen,
  onOpenChange,
  onConfirm,
  isCreating,
  description,
}: CreateDocChronologyDialogProps) => {
  const [includeKeyFacts, setIncludeKeyFacts] = useState(false);
  const [includeRelevantFacts, setIncludeRelevantFacts] = useState(false);
  const [includeAllFacts, setIncludeAllFacts] = useState(false);

  const handleConfirm = () => {
    onConfirm({ includeKeyFacts, includeRelevantFacts, includeAllFacts });
  };

  useEffect(() => {
    if (includeAllFacts) {
      setIncludeKeyFacts(true);
      setIncludeRelevantFacts(true);
    } else {
      setIncludeKeyFacts(false);
      setIncludeRelevantFacts(false);
    }
  }, [includeAllFacts]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--folder-git-2" />
            Create Document Chronology
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="ml-2 space-y-5">
          <div className="flex items-center space-x-4">
            <Checkbox
              id="include-keyfacts"
              checked={includeKeyFacts}
              onCheckedChange={(checked) => setIncludeKeyFacts(checked === true)}
              disabled={includeAllFacts}
            />
            <label
              htmlFor="include-keyfacts"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              <p className="mb-1.75 font-medium">Include key facts</p>
              <p className="text-gray-500">Automatically flag key facts on any added documents.</p>
            </label>
          </div>

          <div className="flex items-center space-x-4">
            <Checkbox
              id="include-relevant-facts"
              checked={includeRelevantFacts}
              onCheckedChange={(checked) => setIncludeRelevantFacts(checked === true)}
              disabled={includeAllFacts}
            />
            <label
              htmlFor="include-relevant-facts"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              <p className="mb-1.75 font-medium">Include relevant facts</p>
              <p className="text-gray-500">Automatically flag relevant facts on any added documents.</p>
            </label>
          </div>

          <div className="flex items-center space-x-4">
            <Checkbox
              id="include-all-facts"
              checked={includeAllFacts}
              onCheckedChange={(checked) => setIncludeAllFacts(checked === true)}
            />
            <label
              htmlFor="include-all-facts"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              <p className="mb-1.75 font-medium">Include all facts</p>
              <p className="text-gray-500">Automatically flag all facts on any added documents.</p>
            </label>
          </div>
        </div>

        <CreateDocChronPreview
          includeKeyFacts={includeKeyFacts}
          includeRelevantFacts={includeRelevantFacts}
          includeAllFacts={includeAllFacts}
        />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={isCreating}>
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
